import {
  PublicClientApplication,
  BrowserAuthError,
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser';
import { ENTRA_CLIENT_ID, ENTRA_TENANT_ID, IS_ENTRA_SIGN_IN_ENABLED } from '@/lib/config';

/**
 * Obtaining a directory token to exchange at `/api/auth/sign-in`.
 *
 * <p>Only the Entra path lives here. The development path — the backend's
 * {@code MockDirectoryVerifier}, which treats the token as an email address — needs
 * nothing from the browser, and the sign-in page offers whichever of the two
 * {@code /api/auth/methods} says the backend accepts. That verifier is wired only in
 * dev and test, and the backend refuses to start elsewhere without a real tenant.
 *
 * <p>Lifted from FxComplianceEngine, where each workaround below was found the
 * hard way against a real tenant. docs/ENTRA.md tells the same story from the
 * registration's side.
 */

/** Both ids or neither; see lib/config.ts. */
export const IS_ENTRA_CONFIGURED = IS_ENTRA_SIGN_IN_ENABLED;

export class EntraNotConfiguredError extends Error {
  constructor() {
    super(
      'Microsoft sign-in needs an Entra app registration. Set VITE_ENTRA_TENANT_ID and '
        + 'VITE_ENTRA_CLIENT_ID to the same values the backend has as ENTRA_TENANT_ID and '
        + 'ENTRA_CLIENT_ID (scripts/dev.sh --entra does this from backend/.env) — the backend checks the client id as the token audience, so a '
        + 'token minted for a different application is refused even with a good signature.',
    );
    this.name = 'EntraNotConfiguredError';
  }
}

let client: PublicClientApplication | null = null;
let starting: Promise<PublicClientApplication> | null = null;

/**
 * One MSAL instance, initialised once and BEFORE anybody clicks anything.
 *
 * <p>MSAL v3 and later require {@code initialize()} before any other call. The
 * obvious thing — initialising inside the click handler — does not work, and the
 * failure is obscure: a popup may only be opened in the direct user-gesture
 * context, and an {@code await} breaks that chain, so the browser blocks it. MSAL
 * has by then recorded that an interaction started, nothing finishes it, and
 * every later attempt fails with {@code interaction_in_progress} rather than with
 * anything about popups.
 *
 * <p>So the sign-in page calls {@link prepareEntra} on mount and the click
 * handler finds an instance already waiting.
 */
async function msal(): Promise<PublicClientApplication> {
  if (!IS_ENTRA_CONFIGURED) {
    throw new EntraNotConfiguredError();
  }
  if (client) {
    return client;
  }
  if (starting) {
    return starting;
  }
  starting = create();
  return starting;
}

async function create(): Promise<PublicClientApplication> {
  const created = new PublicClientApplication({
    auth: {
      clientId: ENTRA_CLIENT_ID,
      authority: `https://login.microsoftonline.com/${ENTRA_TENANT_ID}`,
      // The origin, which is the landing page. msal-browser 5 processes the
      // response where it lands — the option that used to bounce it to whichever
      // page started the sign-in is gone — so AuthProvider completes it there.
      redirectUri: window.location.origin,
    },
    cache: {
      // Session, not local: a shared or borrowed browser should not stay signed
      // in to an internal tool after the tab closes. The application's own
      // session token is held the same way.
      cacheLocation: 'sessionStorage',
    },
  });
  await created.initialize();

  client = created;
  return created;
}

/**
 * Finishes a sign-in that came back by redirect, if one did.
 *
 * <p>MSAL does not always use the popup: it falls back to a full-page redirect,
 * and Microsoft then returns the browser to the redirect URI — the origin, which
 * is the landing page. The token is waiting in
 * {@code handleRedirectPromise()}'s result.
 *
 * <p>The first version of this called that method only to clear stale interaction
 * state and discarded what it returned, so a redirect sign-in completed at
 * Microsoft, came back, and was silently thrown away: the person landed on the
 * public page, signed in as far as the directory was concerned, and not signed in
 * at all as far as this application was concerned. Nothing appeared in any log,
 * because no token ever reached the backend.
 *
 * <p>Returns the ID token where a sign-in has just completed, and null otherwise,
 * which is the ordinary case on every page load.
 */
let completion: Promise<string | null> | null = null;
let problem: string | null = null;

/**
 * Whether this window is the popup the directory is being driven in.
 *
 * <p>MSAL runs a popup sign-in by opening a window, sending it to Microsoft, and
 * polling its URL from the opener until the response appears. The popup is
 * returned to the redirect URI — the origin — so this whole application boots
 * inside it.
 *
 * <p>It must then do nothing. The request MSAL wrote before opening the popup
 * lives in the opener's `sessionStorage`, which a popup does not share, so trying
 * to complete the sign-in here fails with `no_token_request_cache_error` — and
 * navigating away on that failure takes the response out of the URL the opener is
 * still reading, breaking a sign-in that was otherwise about to succeed. The
 * popup's only job is to hold still.
 */
export function isDirectoryPopup(): boolean {
  try {
    const opened = window.opener !== null && window.opener !== undefined
      && window.opener !== window;
    // Both signals, not just the opener. A tab opened from another tab has an
    // opener too, and on the opener alone this window would refuse to complete a
    // full-page redirect sign-in for as long as it lived — trading one silent
    // failure for another. Only a window that was opened by something AND is
    // holding a directory response is MSAL's popup.
    return opened && unreadDirectoryResponse() !== null;
  } catch {
    // Cross-origin opener. Not ours to reason about, so treat it as not a popup.
    return false;
  }
}

/**
 * Where the popup lands: a static page that boots nothing.
 *
 * <p>Registered in Entra alongside the origin, which the full-page redirect flow
 * still uses. Only the popup is sent here, and only because the popup is the flow
 * where this application loading at the redirect URI actively destroys the
 * sign-in.
 */
export const POPUP_REDIRECT_URI = `${window.location.origin}/auth-callback.html`;

export function completeEntraRedirect(): Promise<string | null> {
  // Asked once, answered to everybody who asks.
  //
  // `handleRedirectPromise()` is a one-shot: it yields the token on the first
  // call and null on every call after, because it consumes the response as it
  // reads it. So a second caller — React's StrictMode double-invoking the effect
  // is the one that bit — gets null, and if the first caller has dropped its
  // answer the sign-in is simply gone, with no token, no session and no error.
  // Memoising the promise makes this a value rather than an event.
  completion ??= (async () => {
    if (!IS_ENTRA_CONFIGURED) {
      return null;
    }
    try {
      const instance = await msal();
      const result = await instance.handleRedirectPromise();
      if (result !== null && !result.idToken) {
        problem = 'Microsoft completed the sign-in but returned no ID token. Check that '
          + 'ID tokens are enabled for the single-page application platform.';
      }
      return result?.idToken ?? null;
    } catch (caught) {
      // Kept, not swallowed. A page load must not fail because a directory would
      // not answer — but the reason it would not answer is the single most useful
      // fact available at this point, and discarding it is what made three
      // successive versions of this failure look identical from the outside.
      problem = caught instanceof BrowserAuthError
        ? `${caught.errorCode}: ${caught.errorMessage}`
        : caught instanceof Error
          ? caught.message
          : String(caught);
      return null;
    }
  })();
  return completion;
}

/**
 * Why the redirect produced no token, where a reason was given.
 *
 * <p>Read only after {@link completeEntraRedirect} has resolved to null. Separate
 * from the token so that the ordinary path — no redirect outstanding, no problem —
 * stays a plain value, and so that adding this channel did not change what every
 * existing caller and test already expects back.
 */
export function directoryRedirectProblem(): string | null {
  return problem;
}

/**
 * An authorization response sitting unread in the address bar.
 *
 * <p>Called only after MSAL has declined to produce a token. If Microsoft sent a
 * code or an error and MSAL did not consume it, the sign-in has failed in the one
 * way that otherwise leaves no trace anywhere: the browser is on the landing page,
 * the response is in the URL, and nothing in the application or the backend knows
 * a sign-in was ever attempted.
 *
 * <p>Reads the query string and the fragment, because which one carries the
 * response depends on how the redirect URI is registered — and a mismatch there is
 * the likeliest reason MSAL ignored it.
 */
export function unreadDirectoryResponse(): string | null {
  const query = new URLSearchParams(window.location.search);
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const where = query.has('code') || query.has('error') ? query : fragment;

  const error = where.get('error');
  if (error !== null) {
    // Microsoft's own words. Far more useful than anything paraphrased here.
    const detail = where.get('error_description');
    return `Microsoft refused the sign-in (${error}).${detail ? ` ${detail}` : ''}`;
  }
  if (!where.has('code')) {
    return null;
  }
  return (
    'Microsoft signed you in and returned an authorization code, but the sign-in '
    + 'library did not read it. That normally means the redirect URI is registered '
    + 'under the wrong platform: it must be registered as a Single-page application, '
    + 'not as Web. A Web registration cannot redeem a code from a browser.'
  );
}

/**
 * Warms MSAL up so the click handler does not have to await anything.
 *
 * <p>Called on mount. Failure is silent by design: the button should still be
 * offered, and a real reason given when it is pressed, rather than the page
 * refusing to render because a directory was slow.
 */
export function prepareEntra(): void {
  if (IS_ENTRA_CONFIGURED) {
    void msal().catch(() => undefined);
  }
}

/**
 * An ID token for the signed-in directory account.
 *
 * <p>Silent first, and interactive only when the directory says interaction is
 * required. A popup on every page load is how people learn to click through
 * consent screens without reading them.
 *
 * <p><b>An ID token, not an access token.</b> The backend is not a resource this
 * application calls on the user's behalf — it is the same application, checking
 * that the person is who the directory says. The client id is the audience.
 */
export async function requestEntraIdToken(): Promise<string> {
  const instance = await msal();
  const account: AccountInfo | undefined = instance.getAllAccounts()[0];

  const request = { scopes: ['openid', 'profile', 'email'], account };
  // The popup lands on a page that does nothing. See POPUP_REDIRECT_URI.
  const popupRequest = { ...request, redirectUri: POPUP_REDIRECT_URI };

  if (account) {
    try {
      const silent = await instance.acquireTokenSilent(request);
      if (silent.idToken) {
        return silent.idToken;
      }
    } catch (caught) {
      if (!(caught instanceof InteractionRequiredAuthError)) {
        throw caught;
      }
      // Falls through to the popup, which is what the directory asked for.
    }
  }

  let interactive;
  try {
    interactive = await instance.loginPopup(popupRequest);
  } catch (caught) {
    // An interaction that never finished — a blocked or dismissed popup — leaves
    // MSAL believing one is still running, and it refuses to start another. The
    // state is cleared and the person is told what to do, rather than being shown
    // a library error code and a link to its documentation.
    if (caught instanceof BrowserAuthError
        && caught.errorCode === 'interaction_in_progress') {
      instance.clearCache().catch(() => undefined);
      throw new Error(
        'A previous Microsoft sign-in was interrupted, usually because the popup was '
          + 'blocked or closed. That has been cleared — try again, and allow the popup '
          + 'if the browser asks.',
        { cause: caught },
      );
    }
    if (caught instanceof BrowserAuthError && caught.errorCode === 'popup_window_error') {
      // A blocked popup is the browser's decision, not an error to hand back to
      // somebody who only wants to sign in. The whole page goes to Microsoft
      // instead and comes back to the redirect URI, where AuthProvider completes
      // it. This resolves rather than returning a token: the page is leaving.
      await instance.loginRedirect(request);
      return await new Promise<string>(() => undefined);
    }
    throw caught;
  }
  if (!interactive.idToken) {
    throw new Error(
      'Microsoft returned no ID token. The app registration may not be issuing one — check '
        + 'that ID tokens are enabled for the single-page application platform.',
    );
  }
  return interactive.idToken;
}

/** Ends the directory session as well as this application's. */
export async function endEntraSession(): Promise<void> {
  if (!IS_ENTRA_CONFIGURED || !client) {
    return;
  }
  const account = client.getAllAccounts()[0];
  if (account) {
    await client.clearCache({ account });
  }
}
